import type {
  Maybe,
  PremiumHubTestimonials,
  PremiumHubTestimonialsTestimonialsCollection,
} from 'bundles/professional-certificate-category-pages/__generated__/baseContentfulTypes';
import { UnifiedHubProductVariant } from 'bundles/professional-certificate-category-pages/constants';
import type { DegreeCategoryFieldsFragment } from 'bundles/professional-certificate-category-pages/queries/__generated__/DegreeCategoryFieldsFragment';
import type { DegreesByMarketingType_FieldsFragment as DegreesByMarketingTypeFieldsFragment } from 'bundles/professional-certificate-category-pages/queries/__generated__/GetDegreesByMarketingType';
import type { PremiumProductFieldsFragment } from 'bundles/professional-certificate-category-pages/queries/__generated__/PremiumProductFieldsFragment';
import type {
  Degree,
  PPCDegreeCategory,
  PremiumProductWithMetadata,
  ProductCardProps,
} from 'bundles/professional-certificate-category-pages/types';
import { filterNullOrUndefined } from 'bundles/professional-certificate-category-pages/utils';

export const convertPremiumProductToDegree = (product: PremiumProductFieldsFragment): Degree => {
  const domainIds = product.domainIds.filter(filterNullOrUndefined);
  return {
    ...product,
    domainIds,
  };
};

export const convertDegreeCategoryFieldsToPPCDegreeCategory = (
  degreeCategory: DegreeCategoryFieldsFragment
): PPCDegreeCategory => {
  const { degreeCount } = degreeCategory;
  return {
    ...degreeCategory,
    degreeCount: degreeCount ?? 0,
  };
};

export const convertPremiumProductWithMetadataToPremiumHubProductCardProps = (
  product: PremiumProductWithMetadata
): ProductCardProps => {
  const {
    productVariant,
    id,
    slug,
    name,
    bannerImage,
    upcomingTermDates,
    partners,
    atAGlance,
    partnerContentful,
    programDeadlines,
  } = product;
  const firstPartner = partners?.elements?.[0];
  const convertedPartner = firstPartner
    ? {
        id: firstPartner.id,
        name: firstPartner.name,
        squareLogo: firstPartner.squareLogo ?? '',
        partnerMonotoneLogo: partnerContentful?.partnerMonotoneLogo?.url ?? '',
        partnerLogo: partnerContentful?.partnerLogo?.url ?? '',
      }
    : undefined;
  return {
    id,
    slug,
    name,
    bannerImage: bannerImage ?? undefined,
    degreeLevel: productVariant ?? undefined,
    upcomingTermDates: upcomingTermDates ?? undefined,
    productVariant: UnifiedHubProductVariant.Degree,
    partner: convertedPartner,
    description: atAGlance?.ranking ?? undefined,
    timeFrame: atAGlance?.timeFrame ?? undefined,
    programDeadlines,
  };
};

export const convertTestimonialsForProps = (
  testimonialsSection?: Maybe<PremiumHubTestimonials>
): PremiumHubTestimonialsTestimonialsCollection => {
  const collection: PremiumHubTestimonialsTestimonialsCollection = {
    total: -1,
    skip: -1,
    limit: -1,
    items: [],
  };

  if (testimonialsSection?.testimonialsCollection?.items) {
    const items = testimonialsSection.testimonialsCollection.items.filter(filterNullOrUndefined);
    collection.items = items;
    collection.total = items.length;
  }

  return collection;
};

export const convertDegreeByMarketingTypeToPPCFields = (
  degree: DegreesByMarketingTypeFieldsFragment
): PremiumProductFieldsFragment => {
  return {
    ...degree,
    shortName: degree.shortName ?? '',
    programDeadlines: [],
    __typename: 'PremiumProductCollections_learningProduct',
  };
};

export const getEventingProductTypeFromVariant = (productVariant?: string | null) => {
  if (!productVariant) return undefined;

  switch (productVariant) {
    case UnifiedHubProductVariant.Degree:
      return 'degree';
    case UnifiedHubProductVariant.Mastertrack:
      return 'master_track';
    case UnifiedHubProductVariant.UniversityCertificate:
      return 'premium_certificate';
    case UnifiedHubProductVariant.ProfessionalCertificate:
      return 's12n';
    default:
      return undefined;
  }
};

export const removeProductIdPrefix = (id: string) => id.replace(/^degree~/, '');
